import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Share2 } from 'lucide-react';

/**
 * Renders the "View Brag Card" winner ribbon, shown once the Final is completed.
 * Extracted from TournamentDetail.js to keep the parent component slim.
 *
 * Props:
 *   - tournament:   { name, knockout, ... }
 *   - matches:      tournament_matches list
 *   - tournamentId: string
 *   - getTeamName:  (team_id) => string
 *
 * Returns null when no Final has been completed yet.
 */
export default function ChampionBragCard({ tournament, matches, tournamentId, getTeamName }) {
  const knockoutRounds = tournament.knockout?.rounds || [];
  const finalRound = knockoutRounds.find(r => /final/i.test(r.round) && !/semi/i.test(r.round));
  const finalMatch = finalRound && matches.find(m =>
    finalRound.match_ids.includes(m.match_id) && m.status === 'completed' && m.winner_id
  );
  if (!finalMatch) return null;

  const winnerName = getTeamName(finalMatch.winner_id);
  const loserId = finalMatch.team1_id === finalMatch.winner_id ? finalMatch.team2_id : finalMatch.team1_id;
  const loserName = getTeamName(loserId);

  const handleShareWinner = () => {
    const url = `${window.location.origin}/tournaments/${tournamentId}`;
    const text = `🏆 *${tournament.name.toUpperCase()} CHAMPION* 🏆\n\n🥇 ${winnerName}\n🥈 ${loserName}\n\n👉 ${url}\n— via LoveAll 🏸`;
    if (navigator.share) {
      navigator.share({ url, text }).catch(() => {
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
      });
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    }
  };

  return (
    <Card
      className="border-2 border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 to-blue-500/10 overflow-hidden"
      data-testid="champion-brag-card"
    >
      <CardContent className="p-6 sm:p-8 text-center space-y-3">
        <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">View Brag Card</p>
        <h2 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight leading-tight">
          {tournament.name.replace(/Sunday|Classic|Cup|Open/i, m => m.toUpperCase())} <br />
          <span className="text-emerald-500">Domination</span>
        </h2>
        <div className="py-3">
          <p className="text-[10px] uppercase tracking-widest text-emerald-500 mb-1">Winner</p>
          <div
            className="inline-block bg-emerald-400/90 dark:bg-emerald-500 text-emerald-950 dark:text-white font-heading text-2xl font-black px-6 py-2 rounded-md"
            data-testid="champion-winner"
          >
            {winnerName}
          </div>
          <p className="mt-3 text-sm font-medium text-muted-foreground">defeated</p>
          <p className="mt-1 font-heading text-lg font-bold" data-testid="champion-runner-up">{loserName}</p>
        </div>
        <Button
          onClick={handleShareWinner}
          className="bg-emerald-600 hover:bg-emerald-700 text-white w-full sm:w-auto"
          data-testid="champion-share-btn"
        >
          <Share2 className="mr-2 h-4 w-4" />Share Results on WhatsApp
        </Button>
      </CardContent>
    </Card>
  );
}
